import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { AppContextProvider, useAppContext } from './context/AppContext';

// GA4 type declaration
declare global {
  interface Window {
    gtag: (command: string, action: string, config?: Record<string, unknown>) => void;
  }
}
import WelcomeScreen from './pages/WelcomeScreen';
import Rate from './pages/Rate';
import RateBud from './pages/RateBud';
import Cannabis from './pages/Cannabis';
import Admin from './pages/Admin';
import Battle from './pages/Battle';
import Feed from './pages/Feed';
import Games from './pages/Games';
import RatingDetail from './pages/RatingDetail';
import Profile from './pages/Profile';
import Nav from './components/Nav';
import AnimatedPage from './components/AnimatedPage';

function MainLayout() {
  const { username } = useAppContext();
  const location = useLocation();

  // Track GA4 page_view on route changes
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
      });
    }
  }, [location.pathname]);

  // First-time users see welcome screen
  if (!username) {
    return <WelcomeScreen />;
  }

  return (
    <>
      <Nav />
      <AnimatePresence mode="wait">
        <AnimatedPage key={location.pathname}>
          <Outlet />
        </AnimatedPage>
      </AnimatePresence>
    </>
  );
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const session = localStorage.getItem('jointRaterSession');
  let parsed = null;
  try {
    parsed = session ? JSON.parse(session) : null;
  } catch (e) {
    console.error('Failed to parse session:', e);
    localStorage.removeItem('jointRaterSession');
  }
  if (!parsed?.isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const session = localStorage.getItem('jointRaterSession');
  let parsed = null;
  try {
    parsed = session ? JSON.parse(session) : null;
  } catch (e) {
    console.error('Failed to parse session:', e);
    localStorage.removeItem('jointRaterSession');
  }

  return (
    <div className="min-h-screen bg-cannabis-900">
      <Routes>
        <Route element={<MainLayout />}>
          <Route path="/" element={<Cannabis />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/rate" element={<Rate />} />
          <Route path="/rate-bud" element={<RateBud />} />
          <Route path="/battle" element={<Battle />} />
          <Route path="/games" element={<Games />} />
          <Route path="/profile/:name" element={<Profile />} />
          <Route path="/rating/:id" element={<RatingDetail />} />
          <Route path="/admin" element={<AdminGuard><Admin session={parsed!} /></AdminGuard>} />
        </Route>
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <AppContextProvider>
      <AppRoutes />
    </AppContextProvider>
  );
}
